export interface TickOption {
  id: string
  label: string
}

/** Both empty when the wording gives no clue. */
export interface TickSuggestion {
  positive: string[]
  negative: string[]
}

const NO_SUGGESTION: TickSuggestion = { positive: [], negative: [] }

const OPT_OUT =
  /^(don'?t know|do not know|not sure|unsure|can'?t say|no opinion|prefer not to (say|answer)|none of (these|the above)|not applicable|n\/?a)$/
const NEGATION = /\b(not|no|never|don'?t|doesn'?t|neither)\b/
const NEGATING_PREFIXES = ['dis', 'un', 'in', 'im', 'ir', 'il']
const ANTONYMS: [positive: string, negative: string][] = [
  ['good', 'bad'],
  ['good', 'poor'],
  ['excellent', 'poor'],
  ['excellent', 'terrible'],
  ['positive', 'negative'],
  ['better', 'worse'],
  ['love', 'hate'],
  ['support', 'oppose']
]
const LEADING_NUMBER = /^\s*(-?\d+)/
const RANGE_OR_COUNT = /^\s*-?\d+\s*(?:(?:-|–|—|to)\s*\d|\+|%)/

const normalise = (label: string) =>
  label.toLowerCase().replace(/[‘’]/g, "'").replace(/\s+/g, ' ').trim()

const wordsOf = (labels: string[]) =>
  new Set(labels.flatMap((label) => label.split(/[^a-z0-9']+/)))

const isNegated = (label: string, otherEndWords: Set<string>) => {
  if (NEGATION.test(label)) {
    return true
  }
  const words = label.split(/[^a-z0-9']+/)
  const hasPrefixedStem = words.some((word) =>
    NEGATING_PREFIXES.some(
      (prefix) =>
        word.startsWith(prefix) &&
        word.length - prefix.length >= 3 &&
        otherEndWords.has(word.slice(prefix.length))
    )
  )
  return (
    hasPrefixedStem ||
    ANTONYMS.some(
      ([positive, negative]) =>
        words.includes(negative) && otherEndWords.has(positive)
    )
  )
}

const leadingNumber = (label: string) => Number(LEADING_NUMBER.exec(label)?.[1])

// 5+ consecutive numbers and no ranges or counts ("18-24", "4+"), so bands aren't read as ratings.
const isRatingScale = (labels: string[]) => {
  if (
    labels.length < 5 ||
    !labels.every(
      (label) => LEADING_NUMBER.test(label) && !RANGE_OR_COUNT.test(label)
    )
  ) {
    return false
  }
  const numbers = labels.map(leadingNumber)
  const step = numbers[1] - numbers[0]
  return (
    Math.abs(step) === 1 &&
    numbers.every((n, i) => i === 0 || n - numbers[i - 1] === step)
  )
}

/**
 * Pre-ticks the likely top and bottom of a scale from the option wording, never
 * from position alone: the "Agree (1-5)" preset is stored disagree-first.
 */
export const suggestKpiTicks = (options: TickOption[]): TickSuggestion => {
  const scale = options
    .map((option) => ({ id: option.id, label: normalise(option.label) }))
    .filter(({ label }) => !OPT_OUT.test(label))
  if (scale.length < 2) {
    return NO_SUGGESTION
  }
  const size = scale.length >= 4 ? 2 : 1
  const head = scale.slice(0, size)
  const tail = scale.slice(-size)
  const ids = (end: typeof scale) => end.map(({ id }) => id)

  if (isRatingScale(scale.map(({ label }) => label))) {
    const headIsHigher =
      leadingNumber(head[0].label) > leadingNumber(tail[0].label)
    return headIsHigher
      ? { positive: ids(head), negative: ids(tail) }
      : { positive: ids(tail), negative: ids(head) }
  }

  const headLabels = head.map(({ label }) => label)
  const tailLabels = tail.map(({ label }) => label)
  const headWords = wordsOf(headLabels)
  const tailWords = wordsOf(tailLabels)
  const headNegated = headLabels.every((label) => isNegated(label, tailWords))
  const tailNegated = tailLabels.every((label) => isNegated(label, headWords))
  const headHasNegated = headLabels.some((label) => isNegated(label, tailWords))
  const tailHasNegated = tailLabels.some((label) => isNegated(label, headWords))

  if (headNegated && !tailHasNegated) {
    return { positive: ids(tail), negative: ids(head) }
  }
  if (tailNegated && !headHasNegated) {
    return { positive: ids(head), negative: ids(tail) }
  }
  return NO_SUGGESTION
}
